/**
 * CR220: Layout Studio Preview Deals Hook
 * 
 * Fetches deals for Layout Studio preview from multiple sources:
 * - live: Public snapshot (site_state.active_snapshot_id)
 * - admin: Admin snapshot (site_state.admin_snapshot_id)
 * - staging: Staging environment deals
 * - batch: Specific batch run output
 * - mock: Static mock data (fallback)
 * 
 * Uses the central toUiDeal mapper for consistent shaping across all sources.
 */

import { useState, useCallback, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import type { DealCategory } from "@/lib/types/deals";
import { toUiDeal, toUiDeals, type UIDeal, type RawDealInput } from "@/lib/deals/toUiDeal";
import { hydrateFlyerArtifactsBatch } from "@/lib/deals/fetchFlyerSnippetForDeal";
import { getDealTimeStatus } from "@/lib/deals/dealValidity";

// ================================================================
// CR306: Best Value Sort Helper
// Sorts deals with Active/Upcoming first, then Expired, with % value desc tie-break
// ================================================================

function computeExpiredRank(deal: UIDeal): number {
  // CR432: Use unified helper for timezone-safe status
  const status = getDealTimeStatus(deal);
  return status === "expired" ? 1 : 0;
}

function applySortBestValue(deals: UIDeal[]): UIDeal[] {
  return [...deals].sort((a, b) => {
    // 1. Status: Active/Upcoming (0) before Expired (1)
    const expiredA = computeExpiredRank(a);
    const expiredB = computeExpiredRank(b);
    if (expiredA !== expiredB) return expiredA - expiredB;
    
    // 2. Effective % back descending (nulls last)
    const percentA = a.effective_percent_back ?? -1;
    const percentB = b.effective_percent_back ?? -1;
    if (percentA !== percentB) return percentB - percentA;
    
    // 3. Merchant name alphabetically (case-insensitive)
    const merchantA = (a.merchant || "").toLowerCase();
    const merchantB = (b.merchant || "").toLowerCase();
    if (merchantA !== merchantB) return merchantA.localeCompare(merchantB);
    
    // 4. _previewId for stable sort
    return (a._previewId || "").localeCompare(b._previewId || "");
  });
}

// ================================================================
// TYPES
// ================================================================

export type PreviewDataSource = "live" | "admin" | "staging" | "batch" | "mock";

export interface PreviewDataSourceState {
  source: PreviewDataSource;
  batchRunId: string | null;
}

export interface PreviewDealsMeta {
  source: PreviewDataSource;
  snapshotId: string | null;
  batchRunId: string | null;
  dealCount: number;
  lastRefreshed: string;
  error: string | null;
}

export interface BatchRunOption {
  id: string;
  created_at: string;
  status: string;
  total_staged_items: number | null;
  deduped_count: number | null;
  merchants_in_scope: number | null;
  sources_enabled: string[] | null;
}

const STORAGE_KEY = "ui.layoutStudio.previewDataSource";
const BATCH_RUN_KEY = "ui.layoutStudio.previewBatchRunId";
const DEFAULT_LIMIT = 20;

// ================================================================
// MOCK DATA - with consistent fields for toUiDeal
// ================================================================

const MOCK_DEALS: RawDealInput[] = [
  {
    id: "mock-walmart",
    merchant_name: "Walmart",
    promo_merchant_name: "Walmart",
    reward_brand_name: "Walmart",
    headline_mechanic: "10% off all gift cards",
    deal_category: "DISCOUNT",
    deal_type: "discount",
    discount_percent: 10,
    valid_from: new Date().toISOString(),
    valid_until: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
    effective_percent_back: 10,
    source_type: "website",
    purchase_gift_card_brand: "Walmart",
    gift_card_brand: "Walmart",
  },
  {
    id: "mock-shoppers",
    merchant_name: "Shoppers Drug Mart",
    promo_merchant_name: "Shoppers Drug Mart",
    reward_brand_name: "The Keg",
    headline_mechanic: "Buy $100 in The Keg gift cards and receive a $25 bonus card",
    deal_category: "BONUS",
    deal_type: "bonus",
    bonus_value_cad: 25,
    qualifying_spend_cad: 100,
    valid_from: new Date().toISOString(),
    valid_until: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString(),
    effective_percent_back: 25,
    source_type: "flyer",
    purchase_gift_card_brand: "The Keg",
    gift_card_brand: "The Keg",
    bonus_merchant: "Shoppers",
    fine_print: "Limit 2 per customer. Cannot be combined with other offers.",
  },
  {
    id: "mock-costco",
    merchant_name: "Costco",
    promo_merchant_name: "Costco",
    reward_brand_name: "Costco",
    headline_mechanic: "Members-only: 15% off",
    deal_category: "DISCOUNT",
    deal_type: "discount",
    discount_percent: 15,
    valid_from: new Date().toISOString(),
    valid_until: new Date(Date.now() + 3 * 24 * 60 * 60 * 1000).toISOString(),
    effective_percent_back: 15,
    source_type: "website",
    purchase_gift_card_brand: "Costco",
    gift_card_brand: "Costco",
  },
  {
    id: "mock-loblaws",
    merchant_name: "Loblaws",
    promo_merchant_name: "Loblaws",
    reward_brand_name: "PC Optimum",
    headline_mechanic: "Earn 5,000 PC Optimum Points on $50 gift card purchase",
    deal_category: "POINTS",
    deal_type: "points",
    points_amount: 5000,
    points_program_name: "PC Optimum",
    valid_from: new Date().toISOString(),
    valid_until: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
    effective_percent_back: 10,
    source_type: "flyer",
    purchase_gift_card_brand: "Loblaws",
    gift_card_brand: "Loblaws",
  },
  {
    id: "mock-metro",
    merchant_name: "Metro",
    promo_merchant_name: "Metro",
    reward_brand_name: "Boston Pizza",
    headline_mechanic: "Buy $50, get $10 bonus",
    deal_category: "BONUS",
    deal_type: "bonus",
    bonus_value_cad: 10,
    qualifying_spend_cad: 50,
    valid_from: new Date().toISOString(),
    valid_until: new Date(Date.now() + 21 * 24 * 60 * 60 * 1000).toISOString(),
    effective_percent_back: 20,
    source_type: "flyer",
    purchase_gift_card_brand: "Boston Pizza",
    gift_card_brand: "Boston Pizza",
    fine_print: "Bonus card valid March 1 - April 30. Limit one per customer.",
  },
  {
    id: "mock-sobeys",
    merchant_name: "Sobeys",
    promo_merchant_name: "Sobeys",
    reward_brand_name: "Swiss Chalet",
    headline_mechanic: "Buy $50, get $10 bonus",
    deal_category: "BONUS",
    deal_type: "bonus",
    bonus_value_cad: 10,
    qualifying_spend_cad: 50,
    valid_from: new Date().toISOString(),
    valid_until: new Date(Date.now() + 10 * 24 * 60 * 60 * 1000).toISOString(),
    effective_percent_back: 20,
    source_type: "website",
    purchase_gift_card_brand: "Swiss Chalet",
    gift_card_brand: "Swiss Chalet",
  },
];

// ================================================================
// RE-EXPORT TYPES
// ================================================================

// Re-export UIDeal as PreviewDealData for backward compatibility
export type PreviewDealData = UIDeal;

/** CR306: Sort option IDs matching Layout Studio */
export type PreviewSortOption = "newest" | "best_value" | "expiring_soon" | "merchant_az" | "category_az";

export interface UseLayoutPreviewDealsResult {
  // Data
  deals: UIDeal[];
  meta: PreviewDealsMeta;
  
  // Source state
  source: PreviewDataSource;
  batchRunId: string | null;
  
  // Batch run options
  batchRuns: BatchRunOption[];
  batchRunsLoading: boolean;
  
  // Loading state
  loading: boolean;
  
  // CR306: Sort option
  sortOption: PreviewSortOption;
  
  // Actions
  setSource: (source: PreviewDataSource) => void;
  setBatchRunId: (id: string | null) => void;
  setSortOption: (sort: PreviewSortOption) => void;
  refresh: () => Promise<void>;
  fetchBatchRuns: () => Promise<void>;
}

export function useLayoutPreviewDeals(
  limit: number = DEFAULT_LIMIT,
  initialSortOption: PreviewSortOption = "newest"
): UseLayoutPreviewDealsResult {
  // Persisted state from localStorage
  const [source, setSourceState] = useState<PreviewDataSource>("live");
  const [batchRunId, setBatchRunIdState] = useState<string | null>(null);
  
  // CR306: Sort option state
  const [sortOption, setSortOptionState] = useState<PreviewSortOption>(initialSortOption);
  
  // Data state - use UIDeal type
  const [deals, setDeals] = useState<UIDeal[]>([]);
  const [meta, setMeta] = useState<PreviewDealsMeta>({
    source: "mock",
    snapshotId: null,
    batchRunId: null,
    dealCount: 0,
    lastRefreshed: new Date().toISOString(),
    error: null,
  });
  const [loading, setLoading] = useState(false);
  
  // Batch runs for selector
  const [batchRuns, setBatchRuns] = useState<BatchRunOption[]>([]);
  const [batchRunsLoading, setBatchRunsLoading] = useState(false);
  
  // CR306: Sort option setter
  const setSortOption = useCallback((sort: PreviewSortOption) => {
    setSortOptionState(sort);
  }, []);

  // Load persisted state on mount
  useEffect(() => {
    try {
      const storedSource = localStorage.getItem(STORAGE_KEY);
      if (storedSource && ["live", "admin", "staging", "batch", "mock"].includes(storedSource)) {
        setSourceState(storedSource as PreviewDataSource);
      }
      const storedBatchId = localStorage.getItem(BATCH_RUN_KEY);
      if (storedBatchId) {
        setBatchRunIdState(storedBatchId);
      }
    } catch {
      // localStorage not available
    }
  }, []);

  // Persist source changes
  const setSource = useCallback((newSource: PreviewDataSource) => {
    setSourceState(newSource);
    try {
      localStorage.setItem(STORAGE_KEY, newSource);
    } catch {
      // localStorage not available
    }
  }, []);

  // Persist batch run ID changes
  const setBatchRunId = useCallback((id: string | null) => {
    setBatchRunIdState(id);
    try {
      if (id) {
        localStorage.setItem(BATCH_RUN_KEY, id);
      } else {
        localStorage.removeItem(BATCH_RUN_KEY);
      }
    } catch {
      // localStorage not available
    }
  }, []);

  // Fetch batch runs for selector
  const fetchBatchRuns = useCallback(async () => {
    setBatchRunsLoading(true);
    try {
      const { data, error: queryError } = await supabase
        .from("cr075_batch_runs")
        .select("id, created_at, status, total_staged_items, deduped_count, merchants_in_scope, sources_enabled")
        .eq("is_deleted", false)
        .order("created_at", { ascending: false })
        .limit(25);

      if (queryError) {
        console.error("[useLayoutPreviewDeals] Batch runs query error:", queryError);
        return;
      }

      setBatchRuns(data || []);
    } catch (err) {
      console.error("[useLayoutPreviewDeals] Batch runs fetch error:", err);
    } finally {
      setBatchRunsLoading(false);
    }
  }, []);

  // Main fetch function
  const fetchDeals = useCallback(async () => {
    console.log("[CR220][BatchPreview] Starting fetch with source=", source, "batchRunId=", batchRunId);
    
    // Mock mode: use static data through toUiDeal
    if (source === "mock") {
      const mappedMock = toUiDeals(MOCK_DEALS, "mock").slice(0, limit);
      console.log("[CR220][BatchPreview] Using mock data, count=", mappedMock.length);
      setDeals(mappedMock);
      setMeta({
        source: "mock",
        snapshotId: null,
        batchRunId: null,
        dealCount: mappedMock.length,
        lastRefreshed: new Date().toISOString(),
        error: null,
      });
      return;
    }

    setLoading(true);
    
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        setMeta(prev => ({ ...prev, error: "Authentication required" }));
        setLoading(false);
        return;
      }

      // Map source to API mode
      const modeMap: Record<PreviewDataSource, string> = {
        live: "live",
        admin: "admin",
        staging: "staging",
        batch: "staging", // batch uses staging API mode with specific batch_run_id
        mock: "mock",
      };
      const apiMode = modeMap[source];

      // For staging/batch mode, we need a batch_run_id - either from state or fetch latest
      let effectiveBatchRunId = batchRunId;
      if ((source === "staging" || source === "batch") && !effectiveBatchRunId) {
        // Auto-fetch the most recent batch run
        const { data: latestBatch } = await supabase
          .from("cr075_batch_runs")
          .select("id")
          .eq("is_deleted", false)
          .order("created_at", { ascending: false })
          .limit(1)
          .single();
        
        if (latestBatch?.id) {
          effectiveBatchRunId = latestBatch.id;
          console.log(`[CR220][BatchPreview] Auto-selected latest batch: ${effectiveBatchRunId}`);
        } else {
          console.log("[CR220][BatchPreview] No batch runs available for staging mode");
          setMeta(prev => ({ 
            ...prev, 
            error: "No batch runs available. Create a batch run first.",
            lastRefreshed: new Date().toISOString(),
          }));
          setDeals([]);
          setLoading(false);
          return;
        }
      }

      // Build query params
      const params = new URLSearchParams({ mode: apiMode });
      if ((source === "staging" || source === "batch") && effectiveBatchRunId) {
        params.append("batch_run_id", effectiveBatchRunId);
      }
      params.append("limit", String(limit));

      console.log(`[CR220][BatchPreview] Calling admin-preview-deals with params: ${params.toString()}`);

      const { data, error: fnError } = await supabase.functions.invoke(
        `admin-preview-deals?${params.toString()}`,
        {
          headers: { Authorization: `Bearer ${session.access_token}` },
        }
      );

      if (fnError) {
        console.error("[CR220][BatchPreview] Function error:", fnError);
        setMeta(prev => ({ 
          ...prev, 
          error: fnError.message,
          lastRefreshed: new Date().toISOString(),
        }));
        setDeals([]);
        setLoading(false);
        return;
      }

      if (data.error) {
        console.error("[CR220][BatchPreview] API error:", data.error);
        setMeta(prev => ({ 
          ...prev, 
          error: data.error,
          lastRefreshed: new Date().toISOString(),
        }));
        setDeals([]);
        setLoading(false);
        return;
      }

      // Log raw response
      const rawDeals = data.deals || [];
      console.log(`[CR220][BatchPreview] API returned rawCount=${rawDeals.length}`);

      // CR220: Use central toUiDeal mapper for consistent shaping
      let mappedDeals = toUiDeals(rawDeals as RawDealInput[], source);
      
      // CR306: Apply client-side sorting based on sortOption
      if (sortOption === "best_value") {
        console.log("[CR306] Applying best_value sort (status-prioritized)");
        mappedDeals = applySortBestValue(mappedDeals);
      } else if (sortOption === "merchant_az") {
        mappedDeals = [...mappedDeals].sort((a, b) => 
          (a.merchant || "").toLowerCase().localeCompare((b.merchant || "").toLowerCase())
        );
      } else if (sortOption === "expiring_soon") {
        mappedDeals = [...mappedDeals].sort((a, b) => {
          const aEnd = a.end_date ? new Date(a.end_date).getTime() : Infinity;
          const bEnd = b.end_date ? new Date(b.end_date).getTime() : Infinity;
          return aEnd - bEnd;
        });
      }
      // "newest" is default from API, no additional sorting needed
      
      // CR220 Guardrail: Log date source tiers for debugging
      const tierCounts = mappedDeals.reduce((acc, d) => {
        const tier = d._dateSourceTier || "unknown";
        acc[tier] = (acc[tier] || 0) + 1;
        return acc;
      }, {} as Record<string, number>);
      
      console.log(`[CR220][BatchPreview] mappedCount=${mappedDeals.length}, dateTiers=`, tierCounts);
      console.log("[CR220][BatchPreview] first3 with tiers=", mappedDeals.slice(0, 3).map(d => ({
        merchant: d.merchant,
        deal_type: d.deal_type,
        start_date: d.start_date,
        end_date: d.end_date,
        _dateSourceTier: d._dateSourceTier,
      })));
      
      // CR449: Batch hydrate flyer artifacts for layout preview
      const hydratedDeals = await hydrateFlyerArtifactsBatch(mappedDeals);
      setDeals(hydratedDeals as typeof mappedDeals);
      setMeta({
        source, // Use the actual source (batch/staging/etc), not the API mode
        snapshotId: data.snapshot?.snapshot_id || null,
        batchRunId: data.snapshot?.batch_run_id || effectiveBatchRunId || null,
        dealCount: mappedDeals.length,
        lastRefreshed: new Date().toISOString(),
        error: null,
      });

      console.log(`[CR220][BatchPreview] Fetched ${mappedDeals.length} deals for source=${source}, sortOption=${sortOption}`);
    } catch (err) {
      console.error("[CR220][BatchPreview] Unexpected error:", err);
      setMeta(prev => ({ 
        ...prev, 
        error: String(err),
        lastRefreshed: new Date().toISOString(),
      }));
      setDeals([]);
    } finally {
      setLoading(false);
    }
  }, [source, batchRunId, limit, sortOption]);

  // Refresh function
  const refresh = useCallback(async () => {
    await fetchDeals();
  }, [fetchDeals]);

  // Auto-fetch on source/batchRunId change
  useEffect(() => {
    fetchDeals();
  }, [fetchDeals]);

  // Load batch runs when source is 'batch'
  useEffect(() => {
    if (source === "batch" && batchRuns.length === 0) {
      fetchBatchRuns();
    }
  }, [source, batchRuns.length, fetchBatchRuns]);

  return {
    deals,
    meta,
    source,
    batchRunId,
    batchRuns,
    batchRunsLoading,
    loading,
    sortOption,
    setSource,
    setBatchRunId,
    setSortOption,
    refresh,
    fetchBatchRuns,
  };
}
